import {
  Box,
  Button,
  Chip,
  Icon,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { faChevronDown, faChevronUp, faQuestionCircle } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { privacyPath, termsPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { Card } from "@outschool/ui-legacy-component-library";
import React from "react";

import { useCurrentUserLearners } from "../../hooks/Learners";
import { useUpdateCurrentUserMutation } from "../../queries/CurrentUser";
import { createActivityOwnerLearnerEnrollmentMutation } from "../../queries/EnrollmentMutations";
import { shouldShowCoppa } from "../enrolling/CoppaConsent";

interface EnrollOwnLearnerProps {
  sectionUid: string;
  activity?: {
    hasTeacherSchedule: boolean;
    is_club?: boolean;
    price_cents?: number | null;
    isSelfPaced?: boolean;
  };
  enrollments?: any[];
  onEnrollmentSuccess?: () => void;
}

export const EnrollOwnLearner: React.FC<EnrollOwnLearnerProps> = ({
  sectionUid,
  activity,
  enrollments = [],
  onEnrollmentSuccess,
}) => {
  const { t } = useTranslation(
    "client\\components\\enrollment\\EnrollOwnLearner",
  );
  const { currentUser } = useSession();
  const allLearners = useCurrentUserLearners();

  // Extract enrolled learner UIDs from enrollments
  const enrolledLearnerUids = React.useMemo(() => {
    if (!enrollments) {
      return [];
    }

    return enrollments
      .filter(
        enrollment =>
          enrollment.__typename === "Enrollment" &&
          enrollment.isEnrolled &&
          !enrollment.isWithdrawn &&
          enrollment.learner?.uid,
      )
      .map(enrollment =>
        enrollment.__typename === "Enrollment" ? enrollment.learner!.uid : "",
      )
      .filter(Boolean);
  }, [enrollments]);

  // Filter out learners that are already enrolled
  const learners = allLearners.filter(
    learner => !enrolledLearnerUids.includes(learner.uid),
  );

  const [selectedLearnerUid, setSelectedLearnerUid] =
    React.useState<string>("");
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [enrollmentError, setEnrollmentError] = React.useState<string | null>(
    null,
  );
  const [enrollmentSuccess, setEnrollmentSuccess] = React.useState<
    string | null
  >(null);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const [createEnrollment] = useMutation(
    createActivityOwnerLearnerEnrollmentMutation,
  );
  const [updateCurrentUser] = useUpdateCurrentUserMutation();

  if (
    !currentUser ||
    !learners.length ||
    activity?.hasTeacherSchedule || // Don't show for tutoring classes (hasTeacherSchedule = true)
    activity?.isSelfPaced // Don't show for self-paced classes
  ) {
    return null;
  }

  const handleEnrollClick = async () => {
    if (!selectedLearnerUid) {
      setEnrollmentError(t("Please select a learner"));
      return;
    }

    setIsEnrolling(true);
    setEnrollmentError(null);
    setEnrollmentSuccess(null);

    try {
      const selectedLearner = learners.find(l => l.uid === selectedLearnerUid);

      // Check if COPPA consent is needed before enrollment
      const needsCoppaConsent = shouldShowCoppa({
        parent: currentUser,
        learners: selectedLearner
          ? [
              {
                age: selectedLearner?.age ?? undefined,
              },
            ]
          : [],
        activity: activity
          ? {
              price_cents: activity.price_cents ?? null,
              isClub: activity.is_club || false,
            }
          : undefined,
      });

      // If COPPA consent is needed, update user consent before enrollment
      if (needsCoppaConsent) {
        await updateCurrentUser({
          variables: { consented_to_coppa_notice_at: dayjs().utc().toDate() },
        });
      }

      await createEnrollment({
        variables: {
          sectionUid,
          learnerUid: selectedLearnerUid,
        },
      });

      setEnrollmentSuccess(
        t("Successfully enrolled {{learnerName}}!", {
          learnerName: selectedLearner?.name || "learner",
        }),
      );
      setSelectedLearnerUid("");

      // Call the callback to refresh the enrollment list
      if (onEnrollmentSuccess) {
        onEnrollmentSuccess();
      }
    } catch {
      setEnrollmentError(
        t("Failed to enroll learner. Please try again or contact support."),
      );
    } finally {
      setIsEnrolling(false);
    }
  };

  const defaultValue = t("Select Learner");

  return (
    <Box
      sx={{
        backgroundColor: "common.white",
        height: "fit-content",
        marginBottom: "1em",
      }}
    >
      <Card>
        {/* Header Section */}
        <Box
          sx={{
            marginBottom: isExpanded ? "1.5em" : "0em",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingY: "0.5em",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                }}
              >
                {t("Enroll Your Learner")}
              </Typography>
              <Tooltip
                placement="top"
                title={t(
                  "You can enroll your own learners in this class at no cost. This enrollment will not generate a payout.",
                )}
              >
                <Icon
                  icon={faQuestionCircle}
                  sx={{
                    color: "primary.800",
                    marginLeft: "0.5em",
                    fontSize: "16px",
                  }}
                />
              </Tooltip>
            </Box>
            <Icon
              icon={isExpanded ? faChevronUp : faChevronDown}
              sx={{
                color: "primary.800",
                fontSize: "16px",
              }}
            />
          </Box>
        </Box>

        {/* Collapsible Content */}
        {isExpanded && (
          <>
            {/* Error Message */}
            {enrollmentError && (
              <Box sx={{ marginBottom: "1em" }}>
                <Chip
                  label={enrollmentError}
                  color="error"
                  size="small"
                  sx={{
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      padding: "0.5em",
                    },
                  }}
                />
              </Box>
            )}

            {/* Success Message */}
            {enrollmentSuccess && (
              <Box sx={{ marginBottom: "1em" }}>
                <Chip
                  label={enrollmentSuccess}
                  color="success"
                  size="small"
                  sx={{
                    height: "auto",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      padding: "0.5em",
                    },
                  }}
                />
              </Box>
            )}

            {/* Enrollment Form */}
            <Box
              sx={{
                display: "flex",
                gap: "1em",
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: "200px", flex: 1 }}>
                <Select
                  defaultValue={defaultValue}
                  selectSx={{ borderRadius: 35 }}
                  value={selectedLearnerUid || defaultValue}
                  size="small"
                  onChange={({ target: { value } }) => {
                    if (value !== defaultValue) {
                      setSelectedLearnerUid(value as string);
                    }
                  }}
                  fullWidth
                  disabled={isEnrolling}
                >
                  <MenuItem
                    disabled
                    key="default-learner-option"
                    value={defaultValue}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      {defaultValue}
                    </Typography>
                  </MenuItem>
                  {learners.map((learner: ChildFragmentFragment) => (
                    <MenuItem
                      key={`learner-${learner.uid}`}
                      value={learner.uid}
                    >
                      <Typography>
                        {learner.name || `Learner ${learner.uid}`}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {selectedLearnerUid && (
                <Box
                  sx={{
                    backgroundColor: "grey.50",
                    marginBottom: "1em",
                    padding: 16,
                    borderRadius: 8,
                    width: "100%",
                  }}
                >
                  <Trans t={t}>
                    <Typography variant={"body2"}>
                      By clicking Enroll, I agree to Outschool's{" "}
                      <ExternalLink url={termsPath()}>
                        Terms of Services
                      </ExternalLink>{" "}
                      and{" "}
                      <ExternalLink url={privacyPath()}>
                        Privacy Policy
                      </ExternalLink>
                      .
                    </Typography>
                  </Trans>
                </Box>
              )}

              <Button
                onClick={handleEnrollClick}
                disabled={!selectedLearnerUid || isEnrolling}
                variant="contained"
                sx={{
                  height: "fit-content",
                  paddingX: "1.5em",
                  paddingY: "0.75em",
                }}
                fullWidth
              >
                {isEnrolling ? t("Enrolling...") : t("Enroll")}
              </Button>
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};
